import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import contentData from '../data/content.json';
import TagButton from '../components/TagButton';
import { Helmet } from 'react-helmet-async';

function ContentDetailPage() {
    const { contentId } = useParams();
    const navigate = useNavigate();
    const contentItem = contentData.find(item => item.contentId.toString() === contentId);

    // 관련 콘텐츠(태그 기반) 리스트 추출 (항상 호출)
    const relatedByTag = React.useMemo(() => {
        if (!contentItem) return [];
        const tagList = contentItem.tags && contentItem.tags.length > 0 ? contentItem.tags : contentItem.tag ? [contentItem.tag] : [];
        if (tagList.length === 0) return [];
        return contentData.filter(item =>
            item.contentId !== (contentItem && contentItem.contentId) &&
            ((item.tags && item.tags.some(t => tagList.includes(t))) || (item.tag && tagList.includes(item.tag)))
        ).slice(0, 10);
    }, [contentItem]);

    // contentItem이 없으면 404 페이지를 렌더링
    if (!contentItem) {
        return (
            <>
                <Helmet>
                    {/* ───── 기본 TITLE ───── */}
                    <title>
                        {contentItem
                            ? `${contentItem.title} – AI 콘텐츠 | AI 사이트 허브`
                            : 'AI 콘텐츠 상세 | AI 사이트 허브'}
                    </title>

                    {/* ───── 메타 DESCRIPTION ───── */}
                    <meta
                        name="description"
                        content={
                            contentItem
                                ? `${contentItem.summary || ''} (출처: ${contentItem.source || 'AI 사이트 허브'})`
                                : 'AI 관련 뉴스·블로그·영상 등 다양한 콘텐츠의 상세 정보를 확인하세요.'
                        }
                    />

                    {/* ───── KEYWORDS ───── */}
                    <meta
                        name="keywords"
                        content={[
                            contentItem?.title,
                            ...(contentItem?.tags || []),
                            'AI 뉴스',
                            '생성형 AI',
                            'ChatGPT',
                            'AI 콘텐츠',
                        ]
                            .filter(Boolean)
                            .join(', ')}
                    />

                    {/* ───── 크롤링/정규화 ───── */}
                    <meta name="robots" content="index,follow" />
                    <link rel="canonical" href={window.location.href} />

                    {/* ───── Open Graph / Twitter ───── */}
                    <meta
                        property="og:type"
                        content={
                            contentItem?.type === 'youtube'
                                ? 'video.other'
                                : contentItem?.type === 'news'
                                    ? 'article'
                                    : 'website'
                        }
                    />
                    <meta
                        property="og:title"
                        content={contentItem ? `${contentItem.title} – AI 콘텐츠` : 'AI 콘텐츠 상세'}
                    />
                    <meta
                        property="og:description"
                        content={contentItem?.summary || 'AI 관련 뉴스·영상·블로그 상세 정보'}
                    />
                    <meta
                        property="og:image"
                        content={contentItem?.thumbnail || 'https://www.withaihub.com/og-image.png'}
                    />
                    <meta property="og:url" content={window.location.href} />

                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={contentItem ? contentItem.title : 'AI 콘텐츠 상세'} />
                    <meta name="twitter:description" content={contentItem?.summary || 'AI 콘텐츠 상세 정보'} />
                    <meta name="twitter:image" content={contentItem?.thumbnail || 'https://www.withaihub.com/og-image.png'} />

                    {/* ───── 구조화 데이터 (JSON-LD) ───── */}
                    {contentItem && (
                        <script type="application/ld+json">
                            {JSON.stringify({
                                '@context': 'https://schema.org',
                                '@type':
                                    contentItem.type === 'youtube'
                                        ? 'VideoObject'
                                        : contentItem.type === 'news'
                                            ? 'NewsArticle'
                                            : 'SocialMediaPosting',
                                headline: contentItem.title,
                                description: contentItem.summary,
                                image: contentItem.thumbnail || 'https://www.withaihub.com/og-image.png',
                                datePublished: contentItem.date,
                                author: contentItem.author && {
                                    '@type': 'Person',
                                    name: contentItem.author,
                                },
                                publisher: {
                                    '@type': 'Organization',
                                    name: contentItem.source || 'AI 사이트 허브',
                                    logo: {
                                        '@type': 'ImageObject',
                                        url: 'https://www.withaihub.com/favicon.ico',
                                    },
                                },
                                mainEntityOfPage: {
                                    '@type': 'WebPage',
                                    '@id': window.location.href,
                                },
                                ...(contentItem.type === 'youtube' && {
                                    embedUrl: contentItem.embedUrl,
                                    uploadDate: contentItem.date,
                                    duration: contentItem.duration,
                                }),
                            })}
                        </script>
                    )}
                </Helmet>
                <div className="flex flex-col h-screen bg-gray-50 dark:bg-slate-900">


                    <Header showFitViewButton={false} />
                    <main className="flex-grow flex items-center justify-center text-center">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-800 dark:text-white">404 - Not Found</h1>
                            <p className="text-lg text-gray-600 dark:text-gray-400 mt-2">요청하신 콘텐츠를 찾을 수 없습니다.</p>
                            <button
                                onClick={() => navigate(-1)}
                                className="mt-6 inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                뒤로가기
                            </button>
                        </div>
                    </main>
                    <Footer />
                </div>
            </>
        );
    }

    // 관련 콘텐츠 추천 (같은 태그를 가진 다른 콘텐츠)
    const getRelatedContent = () => {
        return contentData
            .filter(item =>
                item.contentId.toString() !== contentId &&
                item.tags && contentItem.tags &&
                item.tags.some(tag => contentItem.tags.includes(tag))
            )
            .slice(0, 10); // 최대 10개 추천
    };
    const relatedContent = getRelatedContent();

    const renderContent = () => {
        switch (contentItem.type) {
            case 'youtube':
                return (
                    <div className="space-y-6">
                        <div className="aspect-video w-full mb-2">
                            <iframe
                                className="w-full h-full rounded-lg"
                                src={`https://www.youtube.com/embed/${contentItem.url.split('v=')[1]}`}
                                title={contentItem.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center justify-center text-xs text-gray-600 dark:text-gray-400 mb-2">
                            {contentItem.date && <span className="bg-gray-100 text-gray-800 rounded px-2 py-1">{contentItem.date}</span>}
                            {contentItem.tags && contentItem.tags.length > 0 ? (
                                contentItem.tags.map((tag, i) => (
                                    <TagButton key={i} tag={tag} onClick={(t) => navigate(`/search?q=${encodeURIComponent(t)}`)} />
                                ))
                            ) : contentItem.tag ? (
                                <TagButton tag={contentItem.tag} onClick={(t) => navigate(`/search?q=${encodeURIComponent(t)}`)} />
                            ) : (
                                <span className="bg-gray-100 text-gray-500 rounded px-2 py-1">태그 없음</span>
                            )}
                        </div>
                        {contentItem.summary && (
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-2">
                                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">요약</h3>
                                <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">{contentItem.summary}</p>
                            </div>
                        )}
                        <div className="flex justify-center">
                            <a
                                href={contentItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                </svg>
                                YouTube에서 보기
                            </a>
                        </div>
                    </div>
                );

            case 'article':
                return (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm">
                            <div className="flex items-center space-x-2 mb-4">
                                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-medium text-green-600">기사</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                                {contentItem.title}
                            </h2>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-6">
                                <span>출처: {contentItem.source}</span>
                                <span>•</span>
                                <span>관련 태그: {contentItem.tags && contentItem.tags.length > 0 ? contentItem.tags.join(', ') : '태그 없음'}</span>
                            </div>
                            {contentItem.summary && (
                                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">요약</h3>
                                    <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                                        {contentItem.summary}
                                    </p>
                                </div>
                            )}
                            <a
                                href={contentItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                원문 보기
                            </a>
                        </div>
                    </div>
                );

            case 'social':
                return (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm">
                            <div className="flex items-center space-x-2 mb-4">
                                <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                <span className="text-sm font-medium text-purple-600">소셜 미디어</span>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                {contentItem.title}
                            </h2>
                            {contentItem.summary && (
                                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">내용</h3>
                                    <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                                        {contentItem.summary}
                                    </p>
                                </div>
                            )}
                            {contentItem.thumbnail && (
                                <div className="mb-6">
                                    <img
                                        src={contentItem.thumbnail}
                                        alt={contentItem.title}
                                        className="w-full max-w-md rounded-lg shadow-sm"
                                    />
                                </div>
                            )}
                            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-6">
                                <span>소셜 미디어</span>
                                <span>•</span>
                                <span>관련 태그: {contentItem.tags && contentItem.tags.length > 0 ? contentItem.tags.join(', ') : '태그 없음'}</span>
                            </div>
                            <a
                                href={contentItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                원문 보기
                            </a>
                        </div>
                    </div>
                );

            case 'news':
                return (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm">
                            <div className="flex items-center space-x-2 mb-4">
                                <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 002 2H4a2 2 0 01-2-2V5zm3 1h6v4H5V6zm6 6H5v2h6v-2z" clipRule="evenodd" />
                                    <path d="M15 7h1a2 2 0 012 2v5.5a1.5 1.5 0 01-3 0V7z" />
                                </svg>
                                <span className="text-sm font-medium text-blue-600">AI 뉴스</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                                {contentItem.title}
                            </h2>
                            {contentItem.summary && (
                                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">요약</h3>
                                    <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                                        {contentItem.summary}
                                    </p>
                                </div>
                            )}
                            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-6">
                                <span>AI 뉴스</span>
                                <span>•</span>
                                <span>관련 태그: {contentItem.tags && contentItem.tags.length > 0 ? contentItem.tags.join(', ') : '태그 없음'}</span>
                            </div>
                            {contentItem.url && (
                                <a
                                    href={contentItem.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    자세히 보기
                                </a>
                            )}
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="text-center py-8">
                        <p className="text-gray-600 dark:text-gray-400">지원하지 않는 콘텐츠 타입입니다.</p>
                    </div>
                );
        }
    };

    const renderRelatedContent = () => {
        if (relatedContent.length === 0) return null;
        return (
            <div className="mt-12">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    관련 콘텐츠
                </h3>
                <div className="flex space-x-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700">
                    {relatedContent.map(item => (
                        <div
                            key={item.contentId}
                            className="min-w-[220px] max-w-[240px] flex-shrink-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors p-4 flex flex-col justify-between"
                        >
                            <h4 className="font-semibold text-gray-900 dark:text-white text-base mb-2 line-clamp-2 text-center">{item.title}</h4>
                            {item.thumbnail && (
                                <img src={item.thumbnail} alt={item.title} className="w-full h-28 object-cover rounded mb-2" />
                            )}
                            <div className="flex flex-wrap gap-2 items-center justify-center text-xs text-gray-600 dark:text-gray-400 mb-2">
                                {item.date && <span className="bg-gray-100 text-gray-800 rounded px-2 py-1">{item.date}</span>}
                                {item.tags && item.tags.map((tag, i) => (
                                    <TagButton key={i} tag={tag} onClick={(t) => navigate(`/search?q=${encodeURIComponent(t)}`)} />
                                ))}
                            </div>
                            {item.summary && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2 text-center">{item.summary}</p>
                            )}
                            <Link
                                to={`/content/${item.contentId}`}
                                className="mt-2 inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-center"
                            >
                                관련글로 이동
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-slate-900">


            <Header showFitViewButton={false} />
            <main className="w-full max-w-5xl mx-auto py-8 px-4 flex-grow flex flex-col md:flex-row md:gap-8">
                {/* 우측 태그 관련 콘텐츠 리스트 (PC) */}
                <aside className="hidden md:block w-44 flex-shrink-0 order-2">
                    {relatedByTag.length > 0 && (
                        <div className="sticky top-24 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 mb-4 shadow-sm">
                            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">관련 콘텐츠</h3>
                            <ul>
                                {relatedByTag.map((item, idx) => (
                                    <Link
                                        key={item.contentId}
                                        to={`/content/${item.contentId}`}
                                        className={`block pb-2 mb-2 ${idx !== relatedByTag.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''} transition rounded-md`}
                                    >
                                        <div className="w-full flex flex-row items-center gap-2 cursor-pointer">
                                            <div className="w-16 h-10 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700">
                                                <img
                                                    src={item.thumbnail || 'https://placehold.co/128x80?text=No+Image'}
                                                    alt={item.title}
                                                    className="w-full h-full object-cover object-center"
                                                />
                                            </div>
                                            <div className="flex-1 text-base font-bold text-gray-900 dark:text-white truncate text-center">
                                                {item.title}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </ul>
                        </div>
                    )}
                </aside>
                {/* 메인 콘텐츠 영역 */}
                <section className="flex-1 order-1">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 px-6 py-8 mb-8">
                        <div className="mb-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="inline-flex items-center text-blue-600 hover:text-blue-700"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                뒤로가기
                            </button>
                        </div>
                        {/* 메인 콘텐츠 */}
                        {contentItem && (
                            <div className="space-y-6">
                                {/* 1. 타이틀 */}
                                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 text-center">{contentItem.title}</h2>
                                {/* 2. youtube는 영상, 나머지는 썸네일 */}
                                {contentItem.type === 'youtube' ? (
                                    <div className="aspect-video w-full mb-2">
                                        <iframe
                                            className="w-full h-full rounded-lg"
                                            src={`https://www.youtube.com/embed/${contentItem.url.split('v=')[1]}`}
                                            title={contentItem.title}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                ) : (
                                    contentItem.thumbnail && (
                                        <img src={contentItem.thumbnail} alt={contentItem.title} className="w-full max-w-lg rounded-lg shadow mb-2 mx-auto" />
                                    )
                                )}
                                {/* 3. 날짜와 태그 */}
                                <div className="flex flex-wrap gap-2 items-center justify-center text-xs text-gray-600 dark:text-gray-400 mb-2">
                                    {contentItem.date && <span className="bg-gray-100 text-gray-800 rounded px-2 py-1">{contentItem.date}</span>}
                                    {contentItem.tags && contentItem.tags.length > 0 ? (
                                        contentItem.tags.map((tag, i) => (
                                            <TagButton key={i} tag={tag} onClick={(t) => navigate(`/search?q=${encodeURIComponent(t)}`)} />
                                        ))
                                    ) : contentItem.tag ? (
                                        <TagButton tag={contentItem.tag} onClick={(t) => navigate(`/search?q=${encodeURIComponent(t)}`)} />
                                    ) : (
                                        <span className="bg-gray-100 text-gray-500 rounded px-2 py-1">태그 없음</span>
                                    )}
                                </div>
                                {/* 4. 요약 */}
                                {contentItem.summary && (
                                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-2">
                                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">요약</h3>
                                        <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">{contentItem.summary}</p>
                                    </div>
                                )}
                                {/* 5. 버튼 */}
                                <div className="flex justify-center">
                                    {contentItem.type === 'youtube' ? (
                                        <a
                                            href={contentItem.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                            </svg>
                                            YouTube에서 보기
                                        </a>
                                    ) : contentItem.url ? (
                                        <a
                                            href={contentItem.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                            원문 보기
                                        </a>
                                    ) : null}
                                </div>
                            </div>
                        )}
                        {/* 관련 콘텐츠 (아래쪽, 가로 스크롤) */}
                        {renderRelatedContent()}
                    </div>
                    {/* 모바일(우측 공간 없는 경우)에서는 메인 콘텐츠 하단에 노출 */}
                    <div className="block md:hidden my-6">
                        {relatedByTag.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 shadow-sm">
                                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">관련 콘텐츠</h3>
                                <ul>
                                    {relatedByTag.map((item, idx) => (
                                        <Link
                                            key={item.contentId}
                                            to={`/content/${item.contentId}`}
                                            className={`block pb-2 mb-2 ${idx !== relatedByTag.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''} transition rounded-md`}
                                        >
                                            <div className="w-full flex flex-row items-center gap-2 cursor-pointer">
                                                <div className="w-16 h-10 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700">
                                                    <img
                                                        src={item.thumbnail || 'https://placehold.co/128x80?text=No+Image'}
                                                        alt={item.title}
                                                        className="w-full h-full object-cover object-center"
                                                    />
                                                </div>
                                                <div className="flex-1 text-base font-bold text-gray-900 dark:text-white truncate text-center">
                                                    {item.title}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default ContentDetailPage;